import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import CryptoJS from 'crypto-js';
import { useNavigate } from 'react-router-dom';
import { API_URL, customStyles, secret_key, storedData } from './Constants';
import loadingGif from "../assets/loading.gif"
import './Assignments.css'; // reuse your existing styles

const Assignments = () => {
  const navigate = useNavigate();

  // --- Decrypt Token & User Data ---
  const encryptedToken = localStorage.getItem('token');
  let decryptedToken = '';
  if (encryptedToken) {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedToken, secret_key);
      decryptedToken = bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.error('Token decryption error:', error);
    }
  }

  
  let newdecryptedToken = decryptedToken.trim();
  const token = `Bearer ${newdecryptedToken}`;

  let loggedUserData = null;
  if (storedData) {
    const bytes = CryptoJS.AES.decrypt(storedData, secret_key);
    loggedUserData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  }

  // --- State ---
  const [loading, setLoading] = useState(false);
  const [assignments, setAssignments] = useState([]);
  // --- Fetch Assignments Linked to Parent ---
const fetchAssignments = async () => {
  if (!loggedUserData) return;

  setLoading(true);

  try {
    const res = await axios.get(
      `${API_URL}/assignments/${loggedUserData.company_id}/${loggedUserData.parent_no}`,
      {
        headers: {
          Authorization: `CustomToken ${secret_key}`,
        },
      }
    );

    if (res.data.success) {
      setAssignments(res.data.details || []);
    } else {
      Swal.fire("Info", res.data.message || "No linked assignments found", "info");
    }

  } catch (err) {
    console.error("Fetch assignments error:", err);

    const backendMessage =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      JSON.stringify(err?.response?.data);

    const status = err?.response?.status || "No status";

    Swal.fire({
      title: "API Error",
      html: `
        <p><b>Status:</b> ${status}</p>
        <p><b>Message:</b> ${backendMessage}</p>
        <p><b>CustomToken Sent:</b> ${secret_key}</p>
      `,
      icon: "error",
      width: "600px",
    });

  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchAssignments();
  }, []);
  // --- Format Currency ---
  const formatCurrency = (val) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
    }).format(val || 0);


  return (
  <div className="dashboard-container container-fluid position-relative">

  {/* Loading Overlay */}
  {loading && (
    <div className="loading-overlay d-flex align-items-center justify-content-center">
      <img className="loading-img" src={loadingGif} alt="Loading..." />
    </div>
  )}

  {/* Page Header */}
  <header className="dashboard-header py-3 mb-4 border-bottom">
    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
      <h1 className="fw-bold mb-0 d-flex align-items-center">
        <i className="fas fa-calendar-alt me-2 text-primary"></i>
        Assignments
      </h1>

      <nav aria-label="breadcrumb">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item"><a href="/">Home</a></li>
          <li className="breadcrumb-item active">Assignments</li>
        </ol>
      </nav>
    </div>
  </header>

  <div className="row">

    {/* --- Desktop Table --- */}
    <div className="col-12 col-lg-8 mb-4 d-none d-md-block">
      <div className="card shadow-md rounded-4 border-0 h-100">
        <div className="card-header bg-gradient-primary text-white rounded-top-4 py-3 d-flex align-items-center">
          <h5 className="mb-0 d-flex align-items-center">
            <i className="fas fa-book-open me-2"></i>
            Latest Assignments
          </h5>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive rounded-bottom-4">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Student</th>
                  <th>Assignment</th>
                  <th>Course</th>
                  <th>Marks</th>
                  <th>Due Date</th>
                </tr>
              </thead>
              <tbody>
                {assignments?.length ? (
                  assignments.map((assign, i) => (
                    <tr key={i}>
                      <td className="fw-semibold text-dark">
                        <i className="fas fa-user-graduate me-2 text-primary"></i>
                        {assign.full_name || "-"}
                      </td>
                      <td>
                        <div className="assignment-title">{assign.title || "Untitled"}</div>
                        {assign.instructions && (
                          <div
                            className="assignment-instructions-compact text-muted small"
                            dangerouslySetInnerHTML={{ __html: assign.instructions }}
                          />
                        )}
                      </td>
                      <td><span className="badge bg-secondary">{assign.course_code || "-"}</span></td>
                      <td><span className="badge bg-success">{assign.marks ?? 0}</span></td>
                      <td><span className="badge bg-warning text-dark">{new Date(assign.due_date).toLocaleDateString()}</span></td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4">
                      <i className="fas fa-book-dead fa-lg d-block mb-2"></i>
                      No assignments available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    {/* --- Mobile Cards --- */}
    <div className="col-12 d-block d-md-none mb-4">
      {assignments?.length ? (
        assignments.map((assign, i) => (
          <div key={i} className="card shadow-sm rounded-4 mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between mb-2">
                <span className="fw-semibold text-dark">
                  <i className="fas fa-user-graduate me-2 text-primary"></i>
                  {assign.full_name || "-"}
                </span>
                <span className="badge bg-warning text-dark">
                  {new Date(assign.due_date).toLocaleDateString()}
                </span>
              </div>
              <div className="mb-1">
                <strong>Assignment:</strong> {assign.title || "Untitled"}
              </div>
              {assign.instructions && (
                <div className="assignment-instructions-compact text-muted small mb-1" dangerouslySetInnerHTML={{ __html: assign.instructions }} />
              )}
              <div className="d-flex justify-content-between mt-2 flex-wrap gap-2">
                <span><strong>Course:</strong> <span className="badge bg-secondary">{assign.course_code || "-"}</span></span>
                <span><strong>Marks:</strong> <span className="badge bg-success">{assign.marks ?? 0}</span></span>
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="text-center text-muted py-4">
          <i className="fas fa-book-dead fa-lg d-block mb-2"></i>
          No assignments available.
        </div>
      )}
    </div>

    {/* --- Notes Sidebar --- */}
    <div className="col-12 col-lg-4 mb-4">
      <div className="card shadow-lg border-0 rounded-4 sticky-top" style={{ top: "80px" }}>
        <div className="card-header bg-gradient-secondary text-white rounded-top-4 py-3">
          <h5 className="mb-0 d-flex align-items-center">
            <i className="fas fa-info-circle me-2"></i>Notes
          </h5>
        </div>
        <div className="card-body">
          <p className="text-muted mb-3">This section shows the latest assignments given to your child.</p>
          <ul className="list-group list-group-flush small">
            <li className="list-group-item"><i className="fas fa-clock text-warning me-2"></i>Check assignments before the due date.</li>
            <li className="list-group-item"><i className="fas fa-book text-primary me-2"></i>Review instructions carefully before submission.</li>
            <li className="list-group-item"><i className="fas fa-star text-success me-2"></i>Marks show the total score for the assignment.</li>
          </ul>
        </div>
      </div>
    </div>

  </div>
</div>
);

};

export default Assignments;
